/**
 * Recent searches store for the mobile search overlay.
 *
 * A small ordered list of the shopper's own search terms in localStorage,
 * most recent first, de-duplicated case-insensitively and capped. Storage is
 * treated as optional: a throwing or corrupt store reads as empty and writes
 * are dropped, so private browsing or a cleared origin can never break search.
 *
 * Pure module (no DOM) so it can be unit-tested with a fake Storage; the
 * <header-search-overlay> element in ../header.js does the rendering.
 */

export const RECENT_SEARCHES_KEY = "sm_recent_searches";
export const RECENT_SEARCHES_MAX = 5;

function normalize(term) {
  return String(term ?? "")
    .trim()
    .replace(/\s+/g, " ");
}

/**
 * @param {Storage} storage  anything with getItem/setItem (window.localStorage)
 * @param {{ key?: string, max?: number }} [options]
 */
export function createRecentSearches(storage, { key = RECENT_SEARCHES_KEY, max = RECENT_SEARCHES_MAX } = {}) {
  const read = () => {
    try {
      const parsed = JSON.parse(storage.getItem(key) ?? "[]");
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((term) => typeof term === "string" && term.trim() !== "").slice(0, max);
    } catch {
      return [];
    }
  };

  const write = (terms) => {
    try {
      storage.setItem(key, JSON.stringify(terms));
    } catch {
      // Storage unavailable (quota, private mode): the list simply won't persist.
    }
  };

  return {
    list: () => read(),

    add(term) {
      const clean = normalize(term);
      if (!clean) return read();
      const lower = clean.toLowerCase();
      const next = [clean, ...read().filter((existing) => existing.toLowerCase() !== lower)].slice(0, max);
      write(next);
      return next;
    },

    remove(term) {
      const next = read().filter((existing) => existing !== term);
      write(next);
      return next;
    },

    clear() {
      write([]);
      return [];
    },
  };
}
